import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import type { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import type { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { validateReviewEntry } from '../query-api'
import type { ReviewEntryItem } from '../types/reviewEntryItem'
import { buildBulkValidateReviewEntryPayload } from '../utils/buildBulkValidateReviewEntryPayload'
import { getReviewEntryId } from '../utils/getReviewEntryId'
import { removeReviewEntryDraft } from '../utils/reviewEntryDraftStorage'

export interface BulkValidateReviewEntry {
  item: ReviewEntryItem
  supplierId: string
}

interface BulkValidateVariables {
  accounts: Account[]
  entries: BulkValidateReviewEntry[]
  purchaseTaxRates: TaxRate[]
}

interface BulkValidateResult {
  failedCount: number
  validatedCount: number
  validatedEntryIds: string[]
}

interface UseBulkValidateReviewEntriesProps {
  onSuccess?: (validatedEntryIds: string[]) => void
}

interface UseBulkValidateReviewEntriesResult {
  isProcessing: boolean
  validateEntries: (variables: BulkValidateVariables) => void
}

export const useBulkValidateReviewEntries = ({
  onSuccess,
}: UseBulkValidateReviewEntriesProps = {}): UseBulkValidateReviewEntriesResult => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()

  const { isLoading: isProcessing, mutate: validateEntries } = useMutation(
    async ({ accounts, entries, purchaseTaxRates }: BulkValidateVariables): Promise<BulkValidateResult> => {
      const organizationId = organization?.id

      if (!organizationId) {
        return { failedCount: entries.length, validatedCount: 0, validatedEntryIds: [] }
      }

      let validatedCount = 0
      let failedCount = 0
      const validatedEntryIds: string[] = []

      for (const [entryIndex, { item, supplierId }] of entries.entries()) {
        if (!item.documentId) {
          failedCount++
          continue
        }

        try {
          await validateReviewEntry({
            documentId: item.documentId,
            organizationId,
            payload: buildBulkValidateReviewEntryPayload({
              accounts,
              entryIndex,
              item,
              organizationBaseCurrencyId: organization.baseCurrencyId,
              organizationId,
              purchaseTaxRates,
              supplierId,
            }),
          })
          const entryId = getReviewEntryId(item, entryIndex)
          removeReviewEntryDraft({ entryId, organizationId })
          validatedEntryIds.push(entryId)
          validatedCount++
        } catch (error) {
          const trackedError = new Error(
            `Review entry bulk validate: entry failed (${error instanceof Error ? error.name : 'unknown'})`,
          )

          if (error instanceof Error) {
            trackedError.cause = error
          }

          trackError(trackedError)
          failedCount++
        }
      }

      return { failedCount, validatedCount, validatedEntryIds }
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(QueryKeys.ReviewEntries)
      },
      onSuccess: ({ failedCount, validatedCount, validatedEntryIds }) => {
        if (validatedCount > 0) {
          notify({
            id: NotificationKeys.ReviewEntriesBulkValidate,
            message: t('review_entries.bulk_validate.success', { count: validatedCount }),
            variant: 'success',
          })
        }

        if (failedCount > 0) {
          notify({
            id: `${NotificationKeys.ReviewEntriesBulkValidate}-error`,
            message: t('review_entries.bulk_validate.error', { count: failedCount }),
            variant: 'error',
          })
        }

        // preserve the caller-side selection when nothing validated so the user can retry
        if (validatedCount > 0) {
          onSuccess?.(validatedEntryIds)
        }
      },
    },
  )

  return { isProcessing, validateEntries }
}
